import { useState } from "react";
import { trpc } from "@/utils/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Plus, Save } from "lucide-react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

export default function SlaSettings() {
  const { data: policies = [], isLoading, refetch } = trpc.tickets.slaPolicies.getAll.useQuery();
  const createMutation = trpc.tickets.slaPolicies.create.useMutation({ onSuccess: () => { refetch(); setOpen(false); toast.success("SLA policy created"); } });
  const updateMutation = trpc.tickets.slaPolicies.update.useMutation({ onSuccess: () => { refetch(); setOpen(false); toast.success("SLA policy updated"); } });

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const [form, setForm] = useState({ name: "", p1ResponseHrs: 1, p1ResolutionHrs: 4, p2ResponseHrs: 2, p2ResolutionHrs: 8, p3ResponseHrs: 4, p3ResolutionHrs: 24, p4ResponseHrs: 8, p4ResolutionHrs: 48, businessCalendar: "business_hours" as const, pauseOnPending: true });

  const openCreate = () => { setEditing(null); setForm({ name: "", p1ResponseHrs: 1, p1ResolutionHrs: 4, p2ResponseHrs: 2, p2ResolutionHrs: 8, p3ResponseHrs: 4, p3ResolutionHrs: 24, p4ResponseHrs: 8, p4ResolutionHrs: 48, businessCalendar: "business_hours", pauseOnPending: true }); setOpen(true); };
  const openEdit = (p: any) => { setEditing(p); setForm({ name: p.name, p1ResponseHrs: p.p1ResponseHrs, p1ResolutionHrs: p.p1ResolutionHrs, p2ResponseHrs: p.p2ResponseHrs, p2ResolutionHrs: p.p2ResolutionHrs, p3ResponseHrs: p.p3ResponseHrs, p3ResolutionHrs: p.p3ResolutionHrs, p4ResponseHrs: p.p4ResponseHrs, p4ResolutionHrs: p.p4ResolutionHrs, businessCalendar: p.businessCalendar, pauseOnPending: p.pauseOnPending }); setOpen(true); };

  const handleSave = () => {
    if (editing) updateMutation.mutate({ id: editing.id, ...form });
    else createMutation.mutate(form);
  };

  const priorities = ["p1", "p2", "p3", "p4"] as const;

  if (isLoading) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-[#5B2C93]" /></div>;

  return (
    <div className="px-0 py-5 space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-[#6B6B6B]">{policies.length} polic{policies.length !== 1 ? "ies" : "y"} configured</p>
        <Button onClick={openCreate} size="sm" className="bg-[#5B2C93] hover:bg-[#4A2377] text-white gap-1.5"><Plus className="h-4 w-4" />Add Policy</Button>
      </div>

      <div className="rounded-lg border border-[#E0E0E0] overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-[#E0E0E0] bg-[#FAFAFA]">
            <th className="text-left px-6 py-3 text-[11px] font-medium text-[#6B6B6B] uppercase tracking-wider">Name</th>
            <th className="text-left px-6 py-3 text-[11px] font-medium text-[#6B6B6B] uppercase tracking-wider">Calendar</th>
            {priorities.map(p => <th key={p} className="text-center px-3 py-3 text-[11px] font-medium text-[#6B6B6B] uppercase tracking-wider">{p.toUpperCase()} Resp/Res</th>)}
            <th className="text-right px-6 py-3 text-[11px] font-medium text-[#6B6B6B] uppercase tracking-wider">Actions</th>
          </tr></thead>
          <tbody>
            {policies.length === 0 ? (
              <tr><td colSpan={7} className="text-center py-12 text-[#9CA3AF]">No SLA policies configured</td></tr>
            ) : policies.map((p: any) => (
              <tr key={p.id} className="border-b border-[#F0F0F0] last:border-0 hover:bg-[#F5F8FF]">
                <td className="px-6 py-3 font-medium text-[#2C2C2C]">{p.name}</td>
                <td className="px-6 py-3 text-[#6B6B6B]">{p.businessCalendar === "24x7" ? "24/7" : "Business Hours"}</td>
                {priorities.map(pr => <td key={pr} className="text-center px-3 py-3 text-[#6B6B6B]">{(p as any)[`${pr}ResponseHrs`]}h / {(p as any)[`${pr}ResolutionHrs`]}h</td>)}
                <td className="px-6 py-3 text-right"><Button size="sm" variant="outline" className="border-[#E0E0E0]" onClick={() => openEdit(p)}>Edit</Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editing ? "Edit" : "Create"} SLA Policy</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div><label className="text-sm font-medium">Name</label><Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="mt-1" /></div>
            <div><label className="text-sm font-medium">Calendar</label>
              <Select value={form.businessCalendar} onValueChange={v => setForm(f => ({ ...f, businessCalendar: v as any }))}><SelectTrigger className="mt-1"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="business_hours">Business Hours (Sun-Thu)</SelectItem><SelectItem value="24x7">24/7</SelectItem></SelectContent></Select>
            </div>
            {priorities.map(pr => (
              <div key={pr} className="grid grid-cols-2 gap-3">
                <div><label className="text-xs font-medium">{pr.toUpperCase()} Response (hrs)</label><Input type="number" value={(form as any)[`${pr}ResponseHrs`]} onChange={e => setForm(f => ({ ...f, [`${pr}ResponseHrs`]: Number(e.target.value) }))} className="mt-1" /></div>
                <div><label className="text-xs font-medium">{pr.toUpperCase()} Resolution (hrs)</label><Input type="number" value={(form as any)[`${pr}ResolutionHrs`]} onChange={e => setForm(f => ({ ...f, [`${pr}ResolutionHrs`]: Number(e.target.value) }))} className="mt-1" /></div>
              </div>
            ))}
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.pauseOnPending} onChange={e => setForm(f => ({ ...f, pauseOnPending: e.target.checked }))} className="rounded" />Pause SLA clock on Pending status</label>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={handleSave} disabled={!form.name || createMutation.isPending || updateMutation.isPending} className="bg-[#5B2C93] hover:bg-[#4A2377] text-white"><Save className="h-4 w-4 mr-2" />Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
